import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslation from './locales/en';
import faTranslation from './locales/fa';

// Debug log to verify translations are loaded
console.log('🔍 [i18n] Loading translations...');
console.log('🔍 [i18n] EN keys count:', Object.keys(enTranslation).length);
console.log('🔍 [i18n] FA keys count:', Object.keys(faTranslation).length);
console.log('🔍 [i18n] Sample EN keys:', Object.keys(enTranslation).slice(0, 10));

// Supported languages configuration
export const SUPPORTED_LANGUAGES = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    dir: 'ltr',
    flag: '🇬🇧',
  },
  {
    code: 'fa',
    name: 'Persian',
    nativeName: 'فارسی',
    dir: 'rtl',
    flag: '🇮🇷',
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    dir: 'rtl',
    flag: '🇸🇦',
  },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

// Helper function to get language config
export const getLanguageConfig = (code: LanguageCode) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslation
      },
      fa: {
        translation: faTranslation
      }
    },
    fallbackLng: 'en',
    debug: false, // MUST be false to reduce console noise
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: {
      useSuspense: false,
    },
  })
  .then(() => {
    console.log('✅ [i18n] Initialized successfully');
    console.log('✅ [i18n] Current language:', i18n.language);
    console.log('✅ [i18n] Debug mode:', i18n.options.debug);
    
    // Test if keys are accessible
    const testKeys = ['manageBiographyEntries', 'timeline', 'biographyImage'];
    testKeys.forEach(key => {
      const translation = i18n.t(key);
      console.log(`🔑 [i18n] ${key}: "${translation}"`);
    });
  });

// Event listener for language changes
i18n.on('languageChanged', (lng: string) => {
  const config = getLanguageConfig(lng as LanguageCode);
  
  if (config) {
    // Update document direction
    document.documentElement.dir = config.dir;
    document.documentElement.lang = lng;
    
    // Update body class (add, don't replace)
    document.body.classList.remove('rtl', 'ltr');
    document.body.classList.add(config.dir);
    
    console.log(`🌐 Language changed to: ${config.nativeName} (${config.dir})`);
  }
});

// Set initial direction on load
const initialLang = i18n.language || 'en';
const initialConfig = getLanguageConfig(initialLang as LanguageCode);
if (initialConfig) {
  document.documentElement.dir = initialConfig.dir;
  document.documentElement.lang = initialLang;
  document.body.classList.remove('rtl', 'ltr');
  document.body.classList.add(initialConfig.dir);
}

export default i18n;
